import ejs from "ejs/ejs.min.js";

export const invoiceValue = (row) => Number(row.effective_price ?? row.price) || 0;
export const rowBoxes = (row) => Number(row.box_count) || 0;
// areaOrder: area names in delivery priority (from Create Route). Bills in
// those areas come first in that order; the rest keep their original order.
export function groupAssignments(rows, areaOrder = []) {
  const groups = new Map();
  const seen = new Set();
  rows.forEach((row) => {
    if ((!row.delivery_boy_id && !row.delivery_staff_id) || seen.has(String(row.id))) return;
    seen.add(String(row.id));
    const key = row.delivery_staff_id ? `staff:${row.delivery_staff_id}` : String(row.delivery_boy_id);
    const group = groups.get(key) || { id: key, name: row.delivery_boy_name || `Employee ${key}`, rows: [], total: 0 };
    group.rows.push(row); group.total += invoiceValue(row); groups.set(key, group);
  });
  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const rank = new Map();
  areaOrder.forEach((area) => { const key = normalize(area); if (!rank.has(key)) rank.set(key, rank.size); });
  const rankOf = (row) => (rank.has(normalize(row.location_name)) ? rank.get(normalize(row.location_name)) : Number.MAX_SAFE_INTEGER);
  groups.forEach((group) => {
    group.rows = group.rows.map((row, index) => ({ row, index }))
      .sort((a, b) => rankOf(a.row) - rankOf(b.row) || a.index - b.index)
      .map(({ row }) => row);
  });
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export async function createAssignmentSheet(group, date, bitField) {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Assigned Orders');
  sheet.columns = [{ width: 10 }, { width: 25 }, { width: 28 }, { width: 40 }, { width: 12 }, { width: 22 }];
  sheet.addRow([`Assigned Orders — ${date} — ${group.name}`]);
  sheet.mergeCells('A1:F1');
  sheet.getRow(1).font = { bold: true, size: 14 };
  sheet.addRow(['Sr. No.', 'Invoice No.', 'BIT', 'Outlet Name', 'Boxes', 'Invoice Amount']);
  group.rows.forEach((row, index) => sheet.addRow([index + 1, String(row.invoice_number || row.id), String(row[bitField] || ''), row.outlet_name || '', rowBoxes(row), invoiceValue(row)]));
  const last = group.rows.length + 2;
  const totalRow = sheet.addRow(['Total', '', '', '', { formula: `SUM(E3:E${last})`, result: group.rows.reduce((sum, row) => sum + rowBoxes(row), 0) }, { formula: `SUM(F3:F${last})`, result: group.rows.reduce((sum, row) => sum + invoiceValue(row), 0) }]);
  sheet.mergeCells(`A${totalRow.number}:D${totalRow.number}`);
  sheet.getColumn(6).numFmt = '#,##0.00';
  [sheet.getRow(2), totalRow].forEach((row) => {
    row.font = { bold: true };
    row.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } }; });
  });
  sheet.eachRow((row) => { row.alignment = { vertical: 'middle', wrapText: true }; row.height = 28; });
  sheet.views = [{ state: 'frozen', ySplit: 2 }];
  sheet.pageSetup = { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  return workbook;
}

export async function downloadAssignmentSheet(group, date, bitField) {
  const workbook = await createAssignmentSheet(group, date, bitField);
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const link = document.createElement('a');
  link.href = url; link.download = `Assignments_${date}_${group.name.replace(/[^a-z0-9_-]/gi, '_')}.xlsx`;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const pdfMoney = (value) => Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function assignmentSheetHtml(group, date, bitField) {
  const rows = group.rows.map((row, index) => ({
    sr: index + 1,
    invoice: String(row.invoice_number || row.id),
    bit: String(row[bitField] || '—'),
    outlet: row.outlet_name || '',
    boxes: rowBoxes(row),
    amount: pdfMoney(invoiceValue(row)),
  }));
  const total = pdfMoney(group.rows.reduce((sum, row) => sum + invoiceValue(row), 0));
  const boxes = group.rows.reduce((sum, row) => sum + rowBoxes(row), 0);
  return ejs.render(`<!doctype html>
    <html><head><meta charset="utf-8"><title>Assignments_<%= date %>_<%= name %></title>
    <style>
      @page { size: A4; margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #172033; font-size: 12px; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      p { color: #475569; margin: 0 0 12px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th, td { border: 1px solid #94a3b8; padding: 7px 8px; text-align: left; overflow-wrap: anywhere; }
      th { background: #dbeafe; }
      .sr { width: 8%; } .inv { width: 18%; } .bit { width: 18%; } .box { width: 9%; text-align: center; } .amt { width: 16%; text-align: right; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; }
      .total td { font-weight: bold; background: #dbeafe; }
      button { padding: 10px 16px; margin-bottom: 16px; cursor: pointer; }
      @media print { button, .hint { display: none; } }
    </style></head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <p class="hint">Choose Save as PDF in the print dialog to download this sheet.</p>
      <h1>Assigned Orders — <%= name %></h1>
      <p>Date: <%= date %> · <%= rows.length %> orders · <%= boxes %> boxes</p>
      <table><thead><tr><th class="sr">Sr. No.</th><th class="inv">Invoice No.</th><th class="bit">BIT</th><th>Outlet Name</th><th class="box">Boxes</th><th class="amt">Invoice Amount</th></tr></thead>
      <tbody><% rows.forEach(function(row) { %>
        <tr><td><%= row.sr %></td><td><%= row.invoice %></td><td><%= row.bit %></td><td><%= row.outlet %></td><td class="box"><%= row.boxes %></td><td class="amt"><%= row.amount %></td></tr>
      <% }); %>
        <tr class="total"><td colspan="4">Total</td><td class="box"><%= boxes %></td><td class="amt"><%= total %></td></tr>
      </tbody></table>
    </body></html>`, { rows, total, boxes, date, name: group.name });
}

export function printAssignmentSheetPdf(group, date, bitField) {
  const html = assignmentSheetHtml(group, date, bitField);
  const printWindow = window.open('', '_blank', 'width=900,height=900');
  if (!printWindow) throw new Error('Please allow popups to download the assignment PDF.');
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { if (!printWindow.closed) printWindow.print(); }, 250);
}

// A bill's company comes from its salesperson, who may belong to several
// companies ("A, B"), so a bill counts towards each of them.
export const rowCompanies = (row) => String(row.company_name || '').split(',').map((name) => name.trim()).filter(Boolean);
export const assignmentCompanies = (rows) => [...new Set(rows.flatMap(rowCompanies))].sort((a, b) => a.localeCompare(b));
export const ALL_COMPANIES = '__all__';

// Rows for the company-wise report, ordered by company, then the assignment order.
export function companyReportRows(group, date, company) {
  const rows = group.rows.flatMap((row, index) => {
    const companies = rowCompanies(row);
    const matched = company === ALL_COMPANIES ? [companies.join(', ') || '—'] : companies.includes(company) ? [company] : [];
    return matched.map((name) => ({ name, row, index }));
  });
  return rows.sort((a, b) => a.name.localeCompare(b.name) || a.index - b.index).map(({ name, row }, index) => ({
    sr: index + 1,
    deliveryBoy: group.name,
    date,
    company: name,
    staff: row.staff_name || '—',
    area: row.location_name || '—',
    invoice: String(row.invoice_number || row.id),
    outlet: row.outlet_name || '',
    boxes: rowBoxes(row),
    amount: invoiceValue(row),
  }));
}

const companyLabel = (company) => (company === ALL_COMPANIES ? 'All Companies' : company);
const companyFileName = (group, date, company) => `Company_Report_${date}_${group.name}_${companyLabel(company)}`.replace(/[^a-z0-9_-]/gi, '_');

export function companyReportHtml(group, date, company) {
  const reportRows = companyReportRows(group, date, company);
  const rows = reportRows.map((row) => ({ ...row, amount: pdfMoney(row.amount) }));
  const total = pdfMoney(reportRows.reduce((sum, row) => sum + row.amount, 0));
  const boxes = reportRows.reduce((sum, row) => sum + row.boxes, 0);
  return ejs.render(`<!doctype html>
    <html><head><meta charset="utf-8"><title><%= fileName %></title>
    <style>
      @page { size: A4 landscape; margin: 10mm; }
      body { font-family: Arial, sans-serif; color: #172033; font-size: 11px; }
      h1 { font-size: 17px; margin: 0 0 4px; }
      p { color: #475569; margin: 0 0 12px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th, td { border: 1px solid #94a3b8; padding: 6px 7px; text-align: left; overflow-wrap: anywhere; }
      th { background: #dbeafe; }
      .sr { width: 5%; } .date { width: 9%; } .box { width: 6%; text-align: center; } .amt { width: 11%; text-align: right; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; }
      .total td { font-weight: bold; background: #dbeafe; }
      button { padding: 10px 16px; margin-bottom: 16px; cursor: pointer; }
      @media print { button, .hint { display: none; } }
    </style></head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <p class="hint">Choose Save as PDF in the print dialog to download this report.</p>
      <h1>Company-wise Assigned Orders — <%= company %></h1>
      <p>Delivery boy: <%= name %> · Date: <%= date %> · <%= rows.length %> orders · <%= boxes %> boxes</p>
      <table><thead><tr><th class="sr">Sr. No.</th><th>Delivery Boy</th><th class="date">Date</th><th>Company Name</th><th>Staff Name</th><th>Area</th><th>Invoice No.</th><th>Outlet Name</th><th class="box">Boxes</th><th class="amt">Invoice Amount</th></tr></thead>
      <tbody><% rows.forEach(function(row) { %>
        <tr><td><%= row.sr %></td><td><%= row.deliveryBoy %></td><td><%= row.date %></td><td><%= row.company %></td><td><%= row.staff %></td><td><%= row.area %></td><td><%= row.invoice %></td><td><%= row.outlet %></td><td class="box"><%= row.boxes %></td><td class="amt"><%= row.amount %></td></tr>
      <% }); %>
        <tr class="total"><td colspan="8">Total</td><td class="box"><%= boxes %></td><td class="amt"><%= total %></td></tr>
      </tbody></table>
    </body></html>`, { rows, total, boxes, date, name: group.name, company: companyLabel(company), fileName: companyFileName(group, date, company) });
}

export function printCompanyReportPdf(group, date, company) {
  const html = companyReportHtml(group, date, company);
  const printWindow = window.open('', '_blank', 'width=1100,height=900');
  if (!printWindow) throw new Error('Please allow popups to download the company report PDF.');
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { if (!printWindow.closed) printWindow.print(); }, 250);
}

// Only bills still out for delivery can be reassigned or removed.
export const isEditableAssignment = (row) => String(row.packaging_status || '').trim().toLowerCase() === 'out_for_delivery';

// Builds the packaging-status updates for an edited assignment: unticked bills
// go back to the report (packing done); kept bills move to the new delivery
// boy, date and vehicle. Unchanged bills are skipped.
export function assignmentEditRequests(rows, { boy, date, vehicle, keepIds }) {
  const keep = new Set([...keepIds].map(String));
  return rows.filter(isEditableAssignment).flatMap((row) => {
    if (!keep.has(String(row.id))) {
      return [{ row, body: { packagingStatus: 'packing_done', expectedStatus: 'out_for_delivery' } }];
    }
    const unchanged = (row.delivery_staff_id ? `staff:${row.delivery_staff_id}` : String(row.delivery_boy_id)) === String(boy)
      && String(row.delivery_date || '') === String(date)
      && String(row.vehicle_no || '').trim() === String(vehicle).trim();
    if (unchanged) return [];
    return [{
      row,
      body: {
        packagingStatus: 'out_for_delivery', expectedStatus: 'out_for_delivery',
        ...(String(boy).startsWith('staff:') ? { deliveryStaffId: Number(String(boy).slice(6)), deliveryBoyId: null } : { deliveryBoyId: Number(boy) }), deliveryDate: date, vehicleNo: String(vehicle).trim(),
      },
    }];
  });
}
